/**
 * Instacut — usePricing hook
 * ==========================
 * Reads platform_settings via supabase-js (anon key, thanks to the
 * "Public can read platform_settings" RLS policy) and returns the live
 * pricing config used everywhere in the app:
 *
 *   {
 *     standard: { price, includedBarbers, features: [] },
 *     premium:  { price, includedBarbers, features: [] },
 *     extraBarberPrice,        // £/mo per additional barber (Premium)
 *     annualDiscountPct,       // 0-100
 *     loading, error
 *   }
 *
 * Cached at module scope — one fetch per browser session unless
 * `refreshPricing()` is called (used by the admin editor after save).
 */

import { useEffect, useState } from 'react';
import { supabase } from './supabase';

const FALLBACK = {
  standard: { price: 19, includedBarbers: 1, features: [] },
  premium:  { price: 49, includedBarbers: 3, features: [] },
  extraBarberPrice: 8,
  annualDiscountPct: 10,
};

let _cache = null;
let _inflight = null;
const _listeners = new Set();

const fromRow = (row) => {
  if (!row) return FALLBACK;
  return {
    standard: {
      price:           Number(row.standard_price ?? FALLBACK.standard.price),
      includedBarbers: Number(row.standard_included_barbers ?? FALLBACK.standard.includedBarbers),
      features:        Array.isArray(row.standard_features) ? row.standard_features : [],
    },
    premium: {
      price:           Number(row.premium_price ?? FALLBACK.premium.price),
      includedBarbers: Number(row.premium_included_barbers ?? FALLBACK.premium.includedBarbers),
      features:        Array.isArray(row.premium_features) ? row.premium_features : [],
    },
    extraBarberPrice:  Number(row.extra_barber_price ?? FALLBACK.extraBarberPrice),
    annualDiscountPct: Number(row.annual_discount_pct ?? FALLBACK.annualDiscountPct),
  };
};

export const refreshPricing = async () => {
  _inflight = supabase.from('platform_settings').select('*').maybeSingle()
    .then(({ data }) => {
      _cache = fromRow(data);
      _listeners.forEach(fn => fn(_cache));
      return _cache;
    })
    .catch(() => {
      _cache = FALLBACK;
      _listeners.forEach(fn => fn(_cache));
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
};

export const usePricing = () => {
  const [pricing, setPricing] = useState(_cache);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    let alive = true;
    const listener = (val) => { if (alive) { setPricing(val); setLoading(false); } };
    _listeners.add(listener);
    if (_cache) {
      setPricing(_cache);
      setLoading(false);
    } else {
      (_inflight || refreshPricing()).then(() => {
        if (alive) setLoading(false);
      });
    }
    return () => { alive = false; _listeners.delete(listener); };
  }, []);

  return { pricing: pricing || FALLBACK, loading };
};

export const PRICING_FALLBACK = FALLBACK;

/**
 * Compute annual pricing for a plan given the live discount %:
 *   - yearly   = monthly × 12 × (1 − discount%)
 *   - perMonth = yearly / 12 (effective monthly rate on a yearly plan)
 *   - saveMonthly = the total £ saved over 12 months
 * Rounded to whole pounds to keep displayed copy clean.
 */
export const annualPrice = (pricing, planKey /* 'standard' | 'premium' */) => {
  const monthly = Number(pricing?.[planKey]?.price || 0);
  const discPct = Number(pricing?.annualDiscountPct || 0);
  const yearly = Math.round(monthly * 12 * (1 - discPct / 100));
  const perMonth = Math.round(yearly / 12);
  return { yearly, perMonth, saveMonthly: Math.max(0, monthly * 12 - yearly) };
};
